import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Home } from "lucide-react";
import { api } from "../../lib/api";
import { normalizeMenuSortMode, sortMenuItems } from "../../lib/menuSorting";

const SECTION_LABELS = {
  aperitivos: "Aperitivos",
  comidas: "Platos principales",
  montados: "Montados",
  postres: "Postres",
  bebidas: "Bebidas",
};

const SECTION_ORDER = ["aperitivos", "comidas", "montados", "postres", "bebidas"];
const QUICK_LABELS = {
  aperitivos: "Aperitivos",
  comidas: "Platos principales",
  montados: "Montados",
  postres: "Postres",
  bebidas: "Bebidas",
};
const FALLBACK_INTRO = {
  title: "La mesa popular",
  subtitle: "Cocina cercana, de la tierra, para todos los momentos del día.",
};

const getIntroValue = (value, fallback) => {
  const normalized = String(value ?? "").trim();
  return normalized || fallback;
};

export default function Carta() {
  const [items, setItems] = useState([]);
  const [sortMode, setSortMode] = useState("manual");
  const [intro, setIntro] = useState(FALLBACK_INTRO);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/menu"),
      api.get("/content/menu_settings").catch(() => ({ data: { data: {} } })),
      api.get("/content/carta_intro").catch(() => ({ data: { data: {} } })),
    ]).then(([menuResponse, settingsResponse, introResponse]) => {
      const introData = introResponse.data.data || {};
      setItems(menuResponse.data);
      setSortMode(normalizeMenuSortMode(settingsResponse.data.data?.sort_mode));
      setIntro({
        title: getIntroValue(introData.title, FALLBACK_INTRO.title),
        subtitle: getIntroValue(introData.subtitle, FALLBACK_INTRO.subtitle),
      });
    }).finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const map = {};
    for (const it of items) {
      if (!map[it.section]) map[it.section] = [];
      map[it.section].push(it);
    }
    Object.keys(map).forEach((section) => {
      map[section] = sortMenuItems(map[section], sortMode);
    });
    return map;
  }, [items, sortMode]);

  const sections = Object.keys(grouped).filter((section) => SECTION_ORDER.includes(section)).sort((a, b) => {
    const ia = SECTION_ORDER.indexOf(a); const ib = SECTION_ORDER.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  });

  const scrollToSection = (section) => {
    document.getElementById(`menu-${section}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (loading) return <div className="p-8 text-muted-foreground">Cargando carta…</div>;

  return (
    <div className="space-y-8 animate-in fade-in duration-500" data-testid="carta-page">
      <header className="py-4">
        <div className="text-xs uppercase tracking-[0.25em] text-primary font-bold">Carta</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl mt-1">{intro.title}</h1>
        <p className="text-muted-foreground mt-2">{intro.subtitle}</p>
      </header>

      <nav aria-label="Categorias de la carta">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6 lg:max-w-4xl">
          {SECTION_ORDER.map((section) => (
            <button
              key={section}
              type="button"
              onClick={() => scrollToSection(section)}
              className="inline-flex min-h-11 w-full items-center justify-center rounded-full border border-primary/25 bg-white px-3 py-2 text-center text-sm font-bold text-foreground shadow-sm transition hover:border-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 sm:px-4"
            >
              {QUICK_LABELS[section]}
            </button>
          ))}
          <Link
            to="/"
            className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full border border-primary/25 bg-white px-3 py-2 text-center text-sm font-bold text-foreground shadow-sm transition hover:border-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 sm:px-4"
          >
            <Home size={16} />
            Inicio
          </Link>
        </div>
      </nav>

      {sections.map((s) => (
        <section key={s} id={`menu-${s}`} className="scroll-mt-24">
          <div className="flex items-center gap-2 mb-3">
            <h2 className="font-display font-black text-2xl">{SECTION_LABELS[s] || s}</h2>
          </div>
          <ul className="bg-white rounded-2xl border border-border divide-y divide-border overflow-hidden" data-testid={`menu-section-${s}`}>
            {grouped[s].map((it) => (
              <li
                key={it.id}
                className={`px-5 py-4 flex items-start justify-between gap-4 ${it.name.toLowerCase().includes("mimosa de la feria") ? "bg-primary/5" : ""}`}
                data-testid={`menu-item-${it.id}`}
              >
                <div className="min-w-0">
                  <div className="font-display font-bold text-lg flex items-center gap-2">
                    {it.name}
                    {it.name.toLowerCase().includes("mimosa de la feria") && (
                      <span className="text-[10px] uppercase tracking-widest bg-primary text-primary-foreground px-2 py-0.5 rounded-full">Estrella</span>
                    )}
                  </div>
                  {it.description && <div className="text-sm text-muted-foreground mt-0.5">{it.description}</div>}
                </div>
                <div className="font-display font-bold text-primary shrink-0 whitespace-nowrap">{it.price}</div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
